import React from 'react';
import bemBlock, { useBemCN } from '@gik/core/utils/bemBlock';
import { Button, HTMLParser, SvgIcon, UI } from '@gik/ui';
import { CheckoutLauncher, createCartItemFromProduct, useProducts } from '@gik/checkout';
import { inkindStoreApi, useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { InkindPageAPIModel, PatchInkindPageRequest } from '@gik/core/models/gik/InkindPage';
import { openInkindPageSettingsModal } from '@gik/inkind-page';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import { navigateTo } from '@/utils/environment';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import SideFoliage from '@gik/crm/assets/sideFoliageR2.svg';
import SideFoliageMobile from '@gik/crm/assets/sideFoliageR2-mobile.svg';
import { Analytics } from '@gik/core/utils';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { useSupporters } from '@gik/api/crm/supporters';

export type PremiumPageUpgradeModalInitiatedOn = 'supportersPage' | 'inkindPage' | 'inkindPageSettings' | 'profilePage';
export type PremiumPageUpgradeModalVariant = 'viewSupporters' | 'hideSuggestions' | 'generic';

type PatchInkindAfterPremiumUpgrade = Pick<
  PatchInkindPageRequest,
  'enableSuggestedArticles' | 'enableSuggestedProducts'
>;
export interface IPremiumPageUpgradeModalProps {
  initiatedOn: PremiumPageUpgradeModalInitiatedOn;
  variant: PremiumPageUpgradeModalVariant;
  patchInkindRequest?: PatchInkindAfterPremiumUpgrade;
  buttonsPortal?: () => HTMLElement;
  onPurchase?(): void;
  closeModal?(): void;
}

const premiumPageUpgradeProductid = 199038;

// used to disable redirect back to the inkind page from the supporters page
// see onAfterClose
let hasPurchased = false;

export default function PremiumPageUpgradeModalContent({
  initiatedOn,
  variant,
  onPurchase,
  patchInkindRequest,
  closeModal,
  buttonsPortal,
}: IPremiumPageUpgradeModalProps) {
  const bem = useBemCN('premium-page-upgrade-content');
  const isMdUp = useBreakpoint(Breakpoint.MD);
  const { t } = useTranslation();

  const [buttonsPortalEl, setButtonsPortalEl] = React.useState<HTMLElement>();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { revalidate: revalidateSupporters } = useSupporters(initiatedOn === 'supportersPage' ? inkindRouteId : null);
  const { data: products } = useProducts([premiumPageUpgradeProductid]);
  const product = products?.[0];
  const cart = product ? [createCartItemFromProduct(product)] : [];
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);

  React.useEffect(() => {
    // trigger a rerender when the button portal resolves to an html element
    if (buttonsPortal) setButtonsPortalEl(buttonsPortal());
  }, [buttonsPortalEl, buttonsPortal]);

  const title =
    variant === 'viewSupporters'
      ? t(translationKeys.premiumPageUpgradeModalTitle_Supporters)
      : variant === 'hideSuggestions'
      ? t(translationKeys.premiumPageUpgradeModalTitle_Suggestions)
      : t(translationKeys.premiumPageUpgradeModalTitle_Generic);
  const subtitle = t(translationKeys.premiumPageUpgradeModalSubtitle);

  // TODO: review - these will change based on the variant
  const descriptionItems = [
    t(
      variant === 'viewSupporters'
        ? translationKeys.premiumPageUpgradeModalSupportersDescriptionAdvanced
        : translationKeys.premiumPageUpgradeModalSupportersDescriptionSimple
    ),
    t(translationKeys.premiumPageUpgradeModalSuggestionsDescription),
    t(translationKeys.premiumPageUpgradeModalFeeDescription),
    t(translationKeys.premiumPageUpgradeModalMissionDescription),
  ];

  if (variant === 'hideSuggestions') {
    // show hide suggestions item first
    [descriptionItems[0], descriptionItems[1]] = [descriptionItems[1], descriptionItems[0]];
  }

  const upgradeButtonText = 'Upgrade your page';
  // product not loaded yet but we want to show the price right away. This must be kept in sync
  const price = product?.price || 10;

  async function handleOnSuccessfulPurchase() {
    Analytics.fireEvent(
      'PremiumPageUpgrade',
      { initiatedOn, variant },
      () => ['initiatedOn'],
      () => ['variant']
    );

    hasPurchased = true;

    // if disabled suggested articles or products, update the page to disable those suggestions
    if (patchInkindRequest) {
      await patchInkind(inkindRouteId, patchInkindRequest);
    }

    // update inkind page to be premium
    const updatedPage: InkindPageAPIModel = {
      ...inkindPage,
      isPremium: true,
      ...patchInkindRequest,
    };

    mutateInkindPage(updatedPage, true);

    if (initiatedOn === 'supportersPage') {
      // reload so the PO who purchased will see their purchase history entry
      revalidateSupporters();
    }

    onPurchase?.();
  }

  function handleOnCloseSuccessfulPurchase() {
    // Close the premium modal since we have already purchased premium.
    // Since the checkout modal is rendered from this component, the premium
    // page upgrade modal cannot be closed until the checkout modal is closed.
    closeModal?.();
    // launch the page settings modal and scroll to the toggles
    if (variant === 'hideSuggestions' && initiatedOn !== 'inkindPageSettings') {
      // FIXME: back button override issue due to closing modal on same frame
      setTimeout(() => {
        openInkindPageSettingsModal({ inkindRouteId, scrollToSectionId: 'features-section-suggested-items' });
      }, 500);
    }
  }

  function getImage() {
    const suffix = isMdUp ? '' : '-mobile';
    switch (variant) {
      case 'generic':
        return `${getAssetsUrl()}crm/upgrade-generic${suffix}.svg`;
      case 'hideSuggestions':
        return `${getAssetsUrl()}crm/upgrade-suggestions${suffix}.svg`;
      case 'viewSupporters':
        return `${getAssetsUrl()}crm/upgrade-supporters${suffix}.svg`;
      default:
        return null;
    }
  }

  const imageUrl = getImage();

  const buttons = () => {
    return (
      <CheckoutLauncher
        cachedProducts={product ? [product] : undefined}
        cart={cart}
        initialInkindRouteId={inkindRouteId}
        skipProductsCheckoutStep={false}
        onSuccessfulPurchase={handleOnSuccessfulPurchase}
        onCloseSuccessfulPurchase={handleOnCloseSuccessfulPurchase}
        navigateToInkindPageAfterPurchase={initiatedOn === 'profilePage'}
        child={(onClick, isLoading) => (
          <Button
            {...bem('upgrade-button')}
            fullWidth
            variant={'premium-unlocked'}
            disabled={isLoading}
            loading={isLoading}
            onClick={onClick}
          >
            {upgradeButtonText}
          </Button>
        )}
        initiatedOn={initiatedOn}
      />
    );
  };

  return (
    <>
      <article {...bem('content')}>
        <aside {...bem('image')}>
          <img alt="Upgrade modal image" src={imageUrl} />
          {/* <ImageSupporters /> */}
        </aside>
        <div {...bem('main', [{ [`variant-${variant}`]: variant }])}>
          <header {...bem('header')}>
            <h1 {...bem('title')}>{title}</h1>
            <p {...bem('subtitle')}>{subtitle}</p>
          </header>
          <section {...bem('body')}>
            <div {...bem('value-container')}>
              <var {...bem('currency')}>$</var>
              <var {...bem('value-integer')}>{price.toFixed(0)}</var>
              <div {...bem('decimals-unit-container')}>
                <var {...bem('value-decimals')}>{String(((price % 1) * 100).toFixed(0)).padStart(2, '0')}</var>
                <span {...bem('per-unit')}>per page</span>
              </div>
            </div>
            <ul {...bem('description')}>
              {descriptionItems.map((item, i) => (
                <li {...bem('description-item')} key={i}>
                  <SvgIcon {...bem('item-icon')} name={'Check'} />
                  <div {...bem('description-item-text')}>
                    <HTMLParser rawHtml={item} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {!isMdUp ? renderPortal(buttons(), buttonsPortal) : buttons()}
        </div>
      </article>
      <div {...bem('foliage')}>
        {/* NOTE: must use plain svg to assure svg blend modes are used which has better browser compatibility */}
        {isMdUp ? <SideFoliage /> : <SideFoliageMobile />}
      </div>
    </>
  );
}

export type openPremiumPageUpgradeModalProps = {
  initiatedOn: PremiumPageUpgradeModalInitiatedOn;
  variant: PremiumPageUpgradeModalVariant;
  // bring the user back to this inkind page when the modal closes
  inkindRouteId?: string;
  onPurchase?: () => void;
  patchInkindRequest?: PatchInkindAfterPremiumUpgrade;
};

export function openPremiumPageUpgradeModal({
  initiatedOn,
  variant,
  inkindRouteId,
  onPurchase,
  patchInkindRequest,
}: openPremiumPageUpgradeModalProps) {
  const bem = bemBlock('premium-page-upgrade-modal');
  if (inkindRouteId) {
    inkindStoreApi.getState().setInkindRouteId(inkindRouteId);
  }

  const footerId = 'PremiumPageUpgradeModalFooter';

  Analytics.fireEvent(
    'PremiumPageUpgradeModalOpened',
    { initiatedOn, variant },
    () => ['initiatedOn'],
    () => ['variant']
  );

  return UI.dialog(
    ({ close }) => (
      <PremiumPageUpgradeModalContent
        buttonsPortal={() => document.getElementById(footerId)}
        initiatedOn={initiatedOn}
        variant={variant}
        onPurchase={onPurchase}
        patchInkindRequest={patchInkindRequest}
        closeModal={close}
      />
    ),
    {
      className: bem(null, [{ [`variant-${variant}`]: variant }]),
      closable: true,
      shouldCloseOnOverlayClick: false,
      disableSystemStyling: true,
      autowidth: false,
      footer: <ModalButtonFooter centeredButtons={false} id={footerId} />,
      onClose: () => {
        if (initiatedOn === 'supportersPage' && !hasPurchased) {
          // bring user back to the inkind page
          navigateTo(`/inkinds/${inkindRouteId}`);
          // prevent modal from actually closing
          return false;
        }
      },
    }
  );
}
